import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { resolveGenieHealthStatus, resolveRadiusHealthStatus } from '../utils/integrationHealth';

const POLL_INTERVAL_MS = 30000;

const dotClassByStatus = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-500',
};

const labelClassByStatus = {
  ok: 'text-emerald-700',
  warn: 'text-amber-700',
  down: 'text-red-700',
  unknown: 'text-slate-600',
};

const ServiceItem = ({ title, status }) => (
  <div className="inline-flex items-center gap-2 rounded-md border border-[#bfd5ff] bg-white px-2.5 py-1.5">
    <span className={`h-2.5 w-2.5 rounded-full ${dotClassByStatus[status] || dotClassByStatus.unknown}`} />
    <span className="text-[11px] text-slate-700">{title}</span>
    <span className={`text-[11px] font-semibold ${labelClassByStatus[status] || labelClassByStatus.unknown}`}>
      {status === 'ok' ? 'OK' : status === 'warn' ? 'WARN' : status === 'down' ? 'DOWN' : 'N/A'}
    </span>
  </div>
);

const normalizeStatusValue = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (text === 'ok' || text === 'warn' || text === 'down' || text === 'unknown') return text;
  return null;
};

const applyOverrides = (baseHealth, statusOverrides) => {
  if (!statusOverrides || typeof statusOverrides !== 'object') return baseHealth;
  return {
    backend: normalizeStatusValue(statusOverrides.backend) || baseHealth.backend,
    mongodb: normalizeStatusValue(statusOverrides.mongodb) || baseHealth.mongodb,
    genieacs: normalizeStatusValue(statusOverrides.genieacs) || baseHealth.genieacs,
    freeradius: normalizeStatusValue(statusOverrides.freeradius) || baseHealth.freeradius,
  };
};

const InfraServiceHealthPanel = ({ apiBase, getHeaders, onUnauthorized = null, statusOverrides = null, pollIntervalMs = POLL_INTERVAL_MS }) => {
  const [health, setHealth] = useState({
    backend: 'unknown',
    mongodb: 'unknown',
    genieacs: 'unknown',
    freeradius: 'unknown',
  });
  const [lastCheckedAt, setLastCheckedAt] = useState('');

  const checkHealth = useCallback(async () => {
    try {
      const headers = typeof getHeaders === 'function' ? getHeaders() : {};
      if (!headers?.Authorization) return;
      let computedHealth = null;
      try {
        const aggregate = await axios.get(`${apiBase}/integration/health`, { headers });
        const payload = aggregate?.data && typeof aggregate.data === 'object' ? aggregate.data : {};
        computedHealth = {
          backend: 'ok',
          mongodb: payload?.mongodb?.connected === true ? 'ok' : 'down',
          genieacs: resolveGenieHealthStatus(payload?.genieacs),
          freeradius: resolveRadiusHealthStatus(payload?.freeradius),
        };
      } catch (aggregateError) {
        if (aggregateError?.response?.status === 401 && typeof onUnauthorized === 'function') {
          onUnauthorized();
          return;
        }
        const [mongoRes, genieRes, radiusRes] = await Promise.allSettled([
          axios.get(`${apiBase}/notifications`, { headers, params: { limit: 1 } }),
          axios.get(`${apiBase}/genieacs/health`, { headers }),
          axios.get(`${apiBase}/freeradius/customer-status`, { headers, params: { customer_ids: '__health_probe__' } }),
        ]);

        const isUnauthorized = [mongoRes, genieRes, radiusRes].some(
          (result) => result.status === 'rejected' && result.reason?.response?.status === 401
        );
        if (isUnauthorized && typeof onUnauthorized === 'function') {
          onUnauthorized();
          return;
        }

        const backendOk = [mongoRes, genieRes, radiusRes].some((result) => result.status === 'fulfilled');
        const mongoOk = mongoRes.status === 'fulfilled';
        const geniePayload = genieRes.status === 'fulfilled' ? genieRes.value?.data : null;
        const radiusPayload = radiusRes.status === 'fulfilled' ? radiusRes.value?.data : null;

        computedHealth = {
          backend: backendOk ? 'ok' : 'down',
          mongodb: mongoOk ? 'ok' : 'down',
          genieacs: genieRes.status === 'fulfilled' ? resolveGenieHealthStatus(geniePayload) : 'down',
          freeradius: radiusRes.status === 'fulfilled' ? resolveRadiusHealthStatus(radiusPayload) : 'down',
        };
      }

      setHealth(applyOverrides(computedHealth, statusOverrides));
      setLastCheckedAt(
        String(statusOverrides?.checkedAt || '').trim() || new Date().toISOString()
      );
    } catch {
      setHealth((prev) => ({
        ...applyOverrides(prev, statusOverrides),
        backend: 'down',
      }));
      setLastCheckedAt(
        String(statusOverrides?.checkedAt || '').trim() || new Date().toISOString()
      );
    }
  }, [apiBase, getHeaders, onUnauthorized, statusOverrides]);

  useEffect(() => {
    checkHealth();
    const interval = Number.isFinite(Number(pollIntervalMs)) ? Number(pollIntervalMs) : POLL_INTERVAL_MS;
    const timer = window.setInterval(checkHealth, interval);
    return () => window.clearInterval(timer);
  }, [checkHealth, pollIntervalMs]);

  useEffect(() => {
    if (!statusOverrides || typeof statusOverrides !== 'object') return;
    setHealth((prev) => applyOverrides(prev, statusOverrides));
    if (String(statusOverrides.checkedAt || '').trim()) {
      setLastCheckedAt(String(statusOverrides.checkedAt).trim());
    }
  }, [statusOverrides]);

  const lastCheckedText = useMemo(() => {
    if (!lastCheckedAt) return '-';
    try {
      return new Date(lastCheckedAt).toLocaleTimeString('id-ID');
    } catch {
      return '-';
    }
  }, [lastCheckedAt]);

  return (
    <div className="rounded-lg border border-[#93c5fd] bg-[#eaf2ff] px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <ServiceItem title="Backend" status={health.backend} />
        <ServiceItem title="MongoDB" status={health.mongodb} />
        <ServiceItem title="GenieACS" status={health.genieacs} />
        <ServiceItem title="FreeRADIUS" status={health.freeradius} />
      </div>
      <p className="mt-1 text-[10px] text-slate-600">Health check terakhir: {lastCheckedText}</p>
    </div>
  );
};

export default InfraServiceHealthPanel;
